import React, { useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import EllipsisIcon from "../../assets/workflows/ellipse-icon.svg";
import SquarePenIcon from "../../assets/workflows/square-pen-icon.svg";
import TrashIcon from "../../assets/workflows/trash-icon.svg";

import {
  Container,
  ContainerContent,
  Header,
  HeaderName,
  Button,
  WorkflowsContainer,
  WorkflowCardContainer,
  WorkflowsHeader,
  WorkflowName,
  WorkflowDescription,
  WorkflowStatus,
  WorkflowCreatedAt,
  WorkflowActions,
} from "./style";

import SnackbarAlert from "../../Components/Alerts/SnackbarAlert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { CircularProgress } from "@mui/material";
import { Tooltip } from "antd";
import { InfoCircleOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { CHAT_BASE_API_URL, API_BASE_URL } from "../../constant/constant";
import { useSnackbar } from "../../context/SnackBarContext";


const Workflows = () => {
  const { t, i18n } = useTranslation();
  const { isSidebarOpen } = useOutletContext();
  const navigate = useNavigate();

  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentWorkflow, setCurrentWorkflow] = useState(null);
  const [isRunningWorkflow, setIsRunningWorkflow] = useState(false);
  const showSnackbar = useSnackbar();

  const open = Boolean(anchorEl);

  const handleClick = (event, workflow) => {
    setAnchorEl(event.currentTarget);
    setCurrentWorkflow(workflow);
  };

  const handleClose = () => setAnchorEl(null);

  const handleEdit = () => {
    navigate(`/workflows/create?workflow_id=${currentWorkflow._id}`);
    handleClose();
  };

  const handleSnackbar = (severity, message) => {
    setSnackbarSeverity(severity);
    setSnackbarMessage(message);
    setOpenSnackbar(true);
  };

  const formatTimestamp = (date) => {
    const tzOffset = -date.getTimezoneOffset();
    const diffMs = tzOffset * 60 * 1000;
    const localDate = new Date(date.getTime() + diffMs);

    const offsetSign = tzOffset >= 0 ? "+" : "-";
    const offsetHours = String(Math.floor(Math.abs(tzOffset) / 60)).padStart(2, "0");
    const offsetMinutes = String(Math.abs(tzOffset) % 60).padStart(2, "0");

    return `${localDate.toISOString().slice(0, 19)}${offsetSign}${offsetHours}:${offsetMinutes}`;
  };

  const handleRunWorkflow = async () => {
    try {
      setIsRunningWorkflow(true);

      const initialMessage = {
        role: "human",
        content: t("workflows.runPrompt", { name: currentWorkflow.name }),
      };

      const payload = {
        user_id: localStorage.getItem("id"),
        user_email: localStorage.getItem("email"),
        user_name: localStorage.getItem("name"),
        workflow_id: currentWorkflow._id,
        language: i18n.language === "en" ? "english" : "dutch",
        timestamp: formatTimestamp(new Date()),
        timestampinunix: Date.now(),
        document_urls: [],
        messages: [initialMessage],
      };

      const token = localStorage.getItem("token");
      const response = await fetch(
        `${CHAT_BASE_API_URL}/workflows`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": localStorage.getItem("token") || "",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (data.is_dynamic) {
        showSnackbar(data.text, "info");
      } else if (data.status === 200) {
        showSnackbar(t("workflows.respondChat"), "success");
      } else if (data.status === 400) {
        showSnackbar(data.text, "error");
      } else {
        handleSnackbar("error", data.message || t("workflows.runFailed"));
      }
    } catch (error) {
      console.error("Error running workflow:", error);
      handleSnackbar("error", t("workflows.errorOccurred"));
    } finally {
      setIsRunningWorkflow(false);
      handleClose();
    }
  };

  const fetchWorkflows = async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem("id");
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${API_BASE_URL}/api/workflow/getworkflows?user_id=${userId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": token || "",
          },
        }
      );
      const data = await response.json();

      if (data.responseCode === 200) {
        handleSnackbar("success", t("workflows.fetchSuccess"));
        setWorkflows(data.workflows);
      }
      // else if (data.responseCode === 403){
      //   handleSnackbar("error", data.message);
      // }
      if (data.responseCode === 429) {
        showSnackbar(data.message, "error");
      }

    } catch (error) {
      handleSnackbar("error", t("workflows.fetchError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/api/workflow/delete`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": token || "",
          },
          body: JSON.stringify({ workflowIds: [currentWorkflow._id] }),
        },
      );
      const data = await response.json();
      if (data.responseCode === 429) {
        showSnackbar(data.message, "error");
      }

      if (data.responseCode === 200) {
        handleSnackbar("success", data.message);
        fetchWorkflows();
      } else {
        handleSnackbar("error", data.message);
      }
    } catch (error) {
      handleSnackbar("error", t("workflows.errorOccurred"));
    } finally {
      handleClose();
    }
  };

  return (
    <Container
      isOpen={isSidebarOpen}
      style={{
        marginRight: "0rem",
        background: "#F3F1F8",
        height: "100vh",
      }}
    >
      <div style={{ height: "calc(100vh - 4rem)", padding: "2rem" }}>
        <ContainerContent>
          <Header>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <HeaderName>{t("workflows.title")}</HeaderName>

              <Tooltip
                title={t("workflows.tooltip")}
                placement="top"
              >
                <InfoCircleOutlined
                  style={{
                    fontSize: 14,
                    color: "#8c8c8c",
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            </div>

            <Button onClick={() => navigate("/workflows/create")}>
              {t("workflows.create")}
            </Button>
          </Header>

          <WorkflowsContainer>
            <WorkflowsHeader>
              <WorkflowName
                style={{
                  color: "#ffffff",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: "600",
                  lineHeight: "20px",
                }}
              >
                {t("workflows.name")}
              </WorkflowName>
              <WorkflowDescription
                style={{
                  color: "#ffffff",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: "600",
                  lineHeight: "20px",
                }}
              >
                {t("workflows.description")}
              </WorkflowDescription>
              <WorkflowStatus
                style={{
                  color: "#ffffff",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: "600",
                  lineHeight: "20px",
                }}
              >
                {t("workflows.status")}
              </WorkflowStatus>
              <WorkflowCreatedAt
                style={{
                  color: "#ffffff",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: "600",
                  lineHeight: "20px",
                }}
              >
                {t("workflows.createdAt")}
              </WorkflowCreatedAt>
              <WorkflowActions
                style={{
                  color: "#ffffff",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: "600",
                  lineHeight: "20px",
                }}
              >
                {t("workflows.actions")}
              </WorkflowActions>
            </WorkflowsHeader>

            {loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "2rem",
                }}
              >
                <CircularProgress
                  style={{ color: "#36383c", width: "20px", height: "20px" }}
                />
              </div>
            ) : workflows.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "2rem",
                  color: "#36383c",
                }}
              >
                {t("workflows.noWorkflows")}
              </div>
            ) : (
              <div style={{ overflowY: "auto" }}>
                {workflows.map((workflow) => (
                  <WorkflowCard
                    key={workflow.id}
                    workflow={workflow}
                    handleClick={(event) => handleClick(event, workflow)}
                  />
                ))}
              </div>
            )}
          </WorkflowsContainer>
        </ContainerContent>
      </div>

      <SnackbarAlert
        open={openSnackbar}
        setOpen={setOpenSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem
          onClick={handleRunWorkflow}
          disabled={isRunningWorkflow}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            color: "#36383c",
          }}
        >
          {isRunningWorkflow ? (
            <CircularProgress size={16} style={{ color: "#36383c" }} />
          ) : (
            <PlayCircleOutlined style={{ fontSize: 16 }} />
          )}
          {t("workflows.runWorkflow") || "Run Workflow"}
        </MenuItem>
        <MenuItem
          onClick={handleEdit}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            color: "#36383c",
          }}
        >
          <img src={SquarePenIcon} width={16} alt={t("workflows.editView")} />
          {t("workflows.editView")}
        </MenuItem>
        <MenuItem
          onClick={handleDelete}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            color: "#36383c",
          }}
        >
          <img src={TrashIcon} width={16} alt={t("workflows.delete")} />
          {t("workflows.delete")}
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default Workflows;

export const WorkflowCard = ({ workflow, handleClick }) => {
  const { t } = useTranslation();

  return (
    <WorkflowCardContainer>
      <WorkflowName>{workflow.name}</WorkflowName>
      <WorkflowDescription>{workflow.description}</WorkflowDescription>
      <WorkflowStatus>
        {workflow.status ? t("workflows.active") : t("workflows.inactive")}
      </WorkflowStatus>
      <WorkflowCreatedAt>
        {new Date(workflow.createdAt).toLocaleDateString()}
      </WorkflowCreatedAt>
      <WorkflowActions>
        <img src={EllipsisIcon} alt={t("workflows.actions")} onClick={handleClick} />
      </WorkflowActions>
    </WorkflowCardContainer>
  );
};
